import React from "react";
import {
  AutocompleteInput,
  Datagrid,
  Filter,
  FunctionField,
  PaginationLimit,
  ReferenceField,
  ReferenceInput,
  ResourceContextProvider,
  SearchInput,
  TextField,
} from "react-admin";
import { getMonth } from "utils/tools";
import EmptyComponent from "../../subcontractorAdhocFees/components/EmptyComponent";
import CustomMonthPickerInput from "../../../components/CustomMonthPickerInput";
import moment from "moment";
import { PaddingDenseStyled2List } from "../../../components/ListWrapper";
import { StyledButton } from "../../../components/ListShowButton";
import CustomPagination from "../../../components/CustomPagination";

interface IErpTable {
  subcontractorId?: number;
  isFixedHeight?: boolean;
  isShowTitle?: boolean;
  hasIssued?: boolean;
  isFilterByPeriod?: boolean;
}

const SubcontractorErpFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="search" alwaysOn />

    <CustomMonthPickerInput
      label="ERP Period"
      name="SubcontractorErpPeriod"
      source="SubcontractorErpPeriod"
      year={props.filterValues.year}
      month={props.filterValues.month}
      showClearButton
      alwaysOn
    />

    {props.isNotFilteredBySubcontractor && (
      <ReferenceInput
        reference="subcontractors"
        source="SubcontractorId"
        filterToQuery={(searchText) => {
          if (searchText !== "") return { search: searchText };
        }}
        sort={{ field: "name", order: "ASC" }}
        alwaysOn
      >
        <AutocompleteInput optionText="name" />
      </ReferenceInput>
    )}

    <ReferenceInput
      source="SchoolId"
      reference="schools"
      filterToQuery={(searchText) => {
        if (searchText !== "") return { search: searchText };
      }}
      sort={{ field: "name", order: "ASC" }}
      alwaysOn
    >
      <AutocompleteInput optionText="name" />
    </ReferenceInput>
  </Filter>
);

const SubcontractorErpTable = ({
  subcontractorId,
  isFixedHeight,
  isShowTitle,
  hasIssued,
  isFilterByPeriod,
}: IErpTable) => {
  const isNotFilteredBySubcontractor = !subcontractorId;

  let filterByPeriod = {};
  if (isFilterByPeriod) {
    filterByPeriod = {
      month: moment().subtract(1, "months").month() + 1,
      year: moment().subtract(1, "months").year(),
    };
  }

  const ActionButton = (record: ISubcontractorErp) => (
    <>
      <StyledButton
        href={`/#/subcontractorErps/${record.id}/show?subcontractorId=${record.subcontractorId}`}
        variant="outlined"
      >
        Show
      </StyledButton>
      {!record.isInvoiced && (
        <StyledButton
          variant="outlined"
          href={`/#/subcontractorErps/${record.id}?subcontractorId=${record.subcontractorId}`}
        >
          Edit
        </StyledButton>
      )}
    </>
  );

  return (
    <ResourceContextProvider value="subcontractorErps">
      <PaddingDenseStyled2List
        basePath="/subcontractorErps"
        actions={null}
        exporter={false}
        hasCreate={false}
        filters={
          <SubcontractorErpFilter
            isNotFilteredBySubcontractor={isNotFilteredBySubcontractor}
          />
        }
        bulkActionButtons={false}
        filterDefaultValues={{
          ...filterByPeriod,
          SubcontractorId: subcontractorId,
          HasIssued: hasIssued,
        }}
        sort={{ field: "id", order: "DESC" }}
        title={isShowTitle ? "Subcontractor Erps" : " "}
        pagination={
          <CustomPagination
            limit={isFixedHeight ? <EmptyComponent /> : <PaginationLimit />}
          />
        }
      >
        <Datagrid>
          <TextField source="id" emptyText="-" />
          <ReferenceField
            source="subcontractorId"
            label="Subcontractor Identifier"
            reference="subcontractors"
            link={false}
          >
            <TextField source="subcontractorIdentifier" emptyText="-" />
          </ReferenceField>
          <ReferenceField
            source="subcontractorId"
            label="Subcontractor MBC Code"
            reference="subcontractors"
            link={false}
          >
            <TextField source="mbcVendorId" emptyText="-" />
          </ReferenceField>
          <ReferenceField
            source="subcontractorId"
            reference="subcontractors"
            link={false}
          >
            <TextField source="name" emptyText="-" />
          </ReferenceField>
          <ReferenceField source="schoolId" reference="schools" link={false}>
            <TextField source="name" emptyText="-" />
          </ReferenceField>
          <FunctionField
            label="Period"
            render={(record: any) =>
              `${getMonth(record.month)} - ${record.year}`
            }
          />
          <FunctionField
            label="Actions"
            render={(record: any) => <ActionButton {...record} />}
          />
        </Datagrid>
      </PaddingDenseStyled2List>
    </ResourceContextProvider>
  );
};

export default SubcontractorErpTable;
